import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { CheckCircleIcon, CalendarIcon, UserIcon, ArrowRightIcon, HomeIcon } from '@heroicons/react/24/outline'
import confetti from 'canvas-confetti'

const BookingSuccess = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { booking } = location.state || {}

  useEffect(() => {
    // 触发庆祝动画
    const duration = 3 * 1000
    const animationEnd = Date.now() + duration
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 }

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min

    const interval: any = setInterval(() => {
      const timeLeft = animationEnd - Date.now()

      if (timeLeft <= 0) {
        return clearInterval(interval)
      }

      const particleCount = 50 * (timeLeft / duration)
      confetti({
        ...defaults, 
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      })
      confetti({
        ...defaults, 
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      })
    }, 250)

    return () => clearInterval(interval)
  }, [])

  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">页面已过期</h1>
          <p className="text-gray-500 mb-6">无法找到预约信息，请返回首页。</p>
          <Link to="/" className="text-blue-600 hover:underline">返回首页</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-cyan-50 flex items-center justify-center p-4">
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-200/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      </div>
      
      <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-white/50 animate-scaleIn">
        {/* 顶部渐变横幅 */}
        <div className="relative bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500 p-10 text-center overflow-hidden">
          <div className="absolute inset-0 opacity-30" style={{backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.2) 1px, transparent 1px)', backgroundSize: '20px 20px'}}></div>
          <div className="relative">
            <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-xl shadow-emerald-500/30 rotate-3 hover:rotate-0 transition-transform">
              <CheckCircleIcon className="w-14 h-14 text-emerald-500" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">预约成功！</h1>
            <p className="text-emerald-100">您的课程已确认，期待与您相见</p>
          </div>
        </div>

        <div className="p-8">
          {/* 订单详情卡片 */}
          <div className="bg-gradient-to-br from-slate-50 to-emerald-50/30 rounded-2xl border border-slate-100 p-6 mb-8">
            <h3 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-5 flex items-center">
              <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2"></span>
              订单详情
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">课程名称</span>
                <span className="font-bold text-gray-900">{booking.courseName}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">授课导师</span>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <UserIcon className="w-3 h-3 text-white" />
                  </div>
                  <span className="font-medium text-gray-900">{booking.teacherName}</span>
                </div>
              </div>
              
              <div className="w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent my-3"></div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">上课时间</span>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-100">
                  <CalendarIcon className="w-4 h-4 text-emerald-600" />
                  <span className="font-semibold text-emerald-700">{booking.date} {booking.time}</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-2">
                <span className="text-gray-500 text-sm">支付金额</span>
                <span className="text-2xl font-bold text-gray-900">¥{booking.price}</span>
              </div>
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="space-y-3">
            <button 
              onClick={() => navigate('/student/dashboard')}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-4 rounded-xl font-bold hover:shadow-xl hover:shadow-emerald-500/25 transition-all flex items-center justify-center group"
            >
              前往我的课表
              <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </button>
            
            <button 
              onClick={() => navigate('/')}
              className="w-full bg-white text-gray-700 border-2 border-gray-200 py-3.5 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center justify-center"
            >
              <HomeIcon className="w-5 h-5 mr-2 text-gray-400" />
              返回首页
            </button>
          </div>

          {/* 添加到日历提示 */}
          <div className="mt-8 p-4 rounded-xl bg-slate-50 border border-slate-100">
            <p className="text-xs text-slate-500 text-center">
              📧 系统已自动发送邮件确认信，您可以将课程添加到日历中以便提醒
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BookingSuccess

